import getValue from './getValue';
import { decrypt } from '../../../utils/encryption/decrypt';
import intlNumberFormat from 'src/utils/intlOperations/intlNumberFormat';
import getDisplayFormattedDate from './getDisplayFormattedDate';
import getDisplayFormattedDateTimeFromJsDate from './getDisplayFormattedDateTimeFromJsDate';
import getFullTable from 'src/utils/entityOperations/getFullTable';
import { applyFunctionOrReturnArgument } from 'src/utils';
// import getOptionValue from '../../../utils/getOptionValue'
// Utility function to safely parse time string
function parseTimeString(timeString) {
    try {
        const dummyDate = '2000-01-01 '; // Using fixed date to parse time-only strings
        const dt = new Date(Date.parse(dummyDate + timeString));
        return isNaN(dt.getTime()) ? null : dt;
    }
    catch (_a) {
        return null;
    }
}
// Utility function to safely format time
function formatTime(date) {
    if (!date)
        return '';
    try {
        return new Intl.DateTimeFormat([], {
            hour: 'numeric',
            minute: 'numeric'
        }).format(date);
    }
    catch (_a) {
        return '';
    }
}
export default function getDisplayValue(col, row) {
    var _a, _b;
    let res = null;
    const val = getValue(col, row);
    const cellProps = { col, row, value: val };
    if (col.isArray) {
        if (val)
            return `{${val}}`;
        return undefined;
    }
    if (col.insteadOfNullShowString && val === null) {
        return col.insteadOfNullShowString;
    }
    let skipNumericFormatting = false;
    if (col.displayValueGetter) {
        res = col.displayValueGetter(cellProps);
        if (res === undefined || res === null)
            return '';
        if (res.doReturnAsIs)
            return res.value; // this skips further processing, e.g., if we want to return 'disabled' but there is isInteger: true, then 'disabled' would be cast to zero, which we don't want
    }
    else if (col.backendToFronendValueMap && col.backendToFronendValueMap[String(val)]) {
        res = col.backendToFronendValueMap[String(val)];
        skipNumericFormatting = true;
    }
    else if (col.references) {
        let referenceArray = [];
        if (Array.isArray(col.references)) {
            referenceArray = col.references;
        }
        else if (typeof col.references === 'function') {
            // @ts-expect-error
            referenceArray = col.references(undefined, { col, row });
        }
        if (typeof referenceArray[0] === 'string') {
            referenceArray = referenceArray.map((r) => ({ id: r, name: r }));
        }
        if (col.multiSelect) {
            if (val === undefined || !Array.isArray(val) || val.length === 0)
                return '<empty>';
            res = referenceArray
                .filter((r) => val.map((v) => String(v)).includes(String(r.id)))
                .map((r) => r.name)
                .join(', ');
        }
        else {
            if (Array.isArray(referenceArray)) {
                res = (_a = ((referenceArray === null || referenceArray === void 0 ? void 0 : referenceArray.find((r) => String(r.id) === String(val))) || {})) === null || _a === void 0 ? void 0 : _a.name;
            }
        }
        // complexlyReferences handling starts
    }
    else if (col.complexlyReferences) {
        const cr = col.complexlyReferences;
        const rows = typeof cr.table === 'string' ? getFullTable(cr.table) : cr.table;
        const row = ((rows === null || rows === void 0 ? void 0 : rows.find((a) => a[cr.reference_value_field] === val)) || {});
        const labelGetter = (cr.type === 'labelGetter')
            ? cr.labelGetter
            : (r) => r[cr.reference_value_field];
        res = labelGetter(row);
        //
    }
    else if (col.cellReferences && Array.isArray(col.cellReferences({ col, row })) && col.cellReferences({ col, row }).length > 0) {
        res = (_b = ((col.cellReferences({ col, row }).find((r) => String(r.id) === String(val))) || {})) === null || _b === void 0 ? void 0 : _b.name;
    }
    else {
        res = val;
    }
    const formatAsPercent = (col.isPercent || (col.cellIsPercent && col.cellIsPercent({ row, col }))) &&
        !(col.cellReferences && col.cellReferences(cellProps));
    if (!skipNumericFormatting && !col.displayValueGetter && (col.isNumeric || col.isCurrency || col.isInteger || formatAsPercent)) {
        const numRes = Number(res);
        if (isNaN(numRes)) {
            res = '';
        }
        else if (col.isInteger) {
            res = intlNumberFormat(numRes, { style: 'decimal', maximumFractionDigits: 0 });
        }
        else if (formatAsPercent) {
            res = intlNumberFormat(numRes, { style: 'percent', maximumFractionDigits: 2 });
        }
        else if (col.isCurrency) {
            res = intlNumberFormat(numRes, { style: 'currency', currency: 'USD' });
        }
        else if (col.isNumeric) {
            res = intlNumberFormat(numRes, { style: 'decimal', maximumFractionDigits: 2 });
        }
    }
    if (col.showZeroInPlaceOfNull && res === null)
        res = 0;
    if (res) {
        if (col.isTime) {
            const dt = parseTimeString(res);
            res = formatTime(dt);
        }
        else if (col.isDate) {
            res = getDisplayFormattedDate({ dateString: res });
        }
        else if (col.isDateTime) {
            try {
                const dt = new Date(Date.parse(res));
                if (!isNaN(dt.getTime())) {
                    let timeZone;
                    if (col.displayTimeZone) {
                        timeZone = String(col.displayTimeZone);
                    }
                    if (col.displayTimeZoneField) {
                        timeZone = String(row[col.displayTimeZoneField]);
                    }
                    res = getDisplayFormattedDateTimeFromJsDate(dt, Object.assign({ timeZone }, col.dateTimeFormatOptions));
                }
                else {
                    res = '';
                }
            }
            catch (_c) {
                res = '';
            }
        }
    }
    if ((res === null || res === undefined) && !row.isTotalRow && !row.isGrandTotalsRow) {
        if (col.references || col.complexlyReferences || (typeof col.cellReferences === 'function' && Boolean(col.cellReferences(cellProps)))) {
            res = col.textToDisplayWhenReferenceValueIsNotSet || '<empty>';
        }
        else {
            res = '';
        }
    }
    if (col.encrypted)
        res = decrypt(res);
    res = applyFunctionOrReturnArgument(col.displayValue_LastTransformation, res);
    if (col.isCode) {
        return res;
    }
    else {
        return res === 0 ? '0' : String(res || '');
    }
}
